import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import blogPosts from '../../content/blogData';
import { BlogCard } from './BlogCard';
import './Blog.css';

function estimateReadTime(content) {
	const words = content.split(/\s+/).length;
	return Math.max(1, Math.ceil(words / 225));
}

export const BlogPost = () => {
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 750);

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 750);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const slug = window.location.pathname.replace('/blog/', '');
	const post = blogPosts.find(p => p.meta.slug === slug);

	if (!post) {
		return (
			<div className="blog-post-container">
				<div style={{ textAlign: 'center', paddingTop: '4rem' }}>
					<h1 style={{ color: '#ffffff', fontSize: '2rem', marginBottom: '1rem' }}>Post Not Found</h1>
					<p style={{ color: '#9ca3af', marginBottom: '2rem' }}>The article you're looking for doesn't exist.</p>
					<button className="blog-post-back" onClick={() => { window.location.href = '/blog'; }}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
						Back to Blog
					</button>
				</div>
			</div>
		);
	}

	const { meta, content } = post;
	const readTime = estimateReadTime(content);
	const categoryClass = meta.category?.toLowerCase() || 'strategy';
	const dateStr = new Date(meta.date + 'T00:00:00').toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});

	const relatedPosts = blogPosts
		.filter(p => p.meta.slug !== meta.slug && p.meta.category === meta.category)
		.slice(0, 3);

	const articleSchema = {
		'@context': 'https://schema.org',
		'@type': 'Article',
		headline: meta.title,
		description: meta.description,
		datePublished: meta.date,
		author: { '@type': 'Organization', name: 'WagerWiz' },
		publisher: {
			'@type': 'Organization',
			name: 'WagerWiz',
			url: 'https://www.thewagerwiz.com',
		},
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': `https://www.thewagerwiz.com/blog/${meta.slug}`,
		},
	};

	const faqItems = [];
	const faqRegex = /## FAQ[\s\S]*$/;
	const faqMatch = content.match(faqRegex);
	if (faqMatch) {
		const questions = faqMatch[0].split(/### /).slice(1);
		questions.forEach(q => {
			const lines = q.trim().split('\n');
			const question = lines[0].replace(/\??\s*$/, '?');
			const answer = lines.slice(1).join(' ').trim();
			if (question && answer) {
				faqItems.push({ question, answer });
			}
		});
	}

	const faqSchema = faqItems.length > 0 ? {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqItems.map(item => ({
			'@type': 'Question',
			name: item.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.answer,
			},
		})),
	} : null;

	return (
		<div className="blog-post-container">
			<Helmet>
				<title>{meta.title} | WagerWiz Blog</title>
				<meta name="description" content={meta.description} />
				<link rel="canonical" href={`https://www.thewagerwiz.com/blog/${meta.slug}`} />
				<meta property="og:title" content={meta.title} />
				<meta property="og:description" content={meta.description} />
				<meta property="og:type" content="article" />
				<meta property="og:url" content={`https://www.thewagerwiz.com/blog/${meta.slug}`} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={meta.title} />
				<meta name="twitter:description" content={meta.description} />
				<meta property="article:published_time" content={meta.date} />
				{meta.tags?.map(tag => (
					<meta key={tag} property="article:tag" content={tag} />
				))}
				<script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
				{faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
			</Helmet>

			<button className="blog-post-back" onClick={() => { window.location.href = '/blog'; }}>
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
				Back to Blog
			</button>

			<article className="blog-post-article">
				<div className="blog-post-meta">
					<span className={`blog-post-category ${categoryClass}`}>{meta.category}</span>
					<span className="blog-post-date">{dateStr}</span>
					<span className="blog-post-read-time">{readTime} min read</span>
				</div>

				<h1 className="blog-post-title">{meta.title}</h1>
				<p className="blog-post-description">{meta.description}</p>

				<div className="blog-post-content">
					<ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
				</div>

				{meta.tags && meta.tags.length > 0 && (
					<div className="blog-post-tags">
						{meta.tags.map(tag => (
							<span key={tag} className="blog-tag">#{tag}</span>
						))}
					</div>
				)}
			</article>

			<div className="blog-cta">
				<h3>Find Your Edge with WagerWiz</h3>
				<p>
					Stop guessing. Use data-driven tools to find +EV bets, arbitrage opportunities, and the best odds across sportsbooks.
				</p>
				<button className="blog-cta-btn" onClick={() => { window.location.href = '/ev-scanner'; }}>
					Try Free EV Scanner
				</button>
			</div>

			{relatedPosts.length > 0 && (
				<div className="blog-related">
					<h3>Related Articles</h3>
					<div className="blog-related-grid">
						{relatedPosts.map(rp => (
							<BlogCard key={rp.meta.slug} post={rp} />
						))}
					</div>
				</div>
			)}
		</div>
	);
};
